// OPTIONS ANALYSIS · Settings (per user). Changing only the entry price mode never rescans.
import React, { useEffect, useState } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { ScannerStatusBar, usePoll } from "@/components/options/OptionsCommon";
import { SELL_WINDOW } from "@/lib/optionsText";
import ResetDefaultsButton from "@/components/ResetDefaultsButton";

// trader settings (always visible); everything after min_oi is technical and lives under Advanced
const NUM = [
  ["min_oi", "Minimum OI (both legs)", "Applies to CE and PE, in both price modes"],
  ["max_leg_spread_pct", "OFFER mode: max bid-offer spread % per leg", "Same limit as the Trade Ticket: a wider book is not a current OFFER opportunity"],
  ["max_itm_calls", "Max ITM calls (≤25)", "Nearest ITM calls, outward"],
  ["max_itm_puts", "Max ITM puts (≤25)", "Nearest ITM puts, outward"],
  ["max_trade_age_s", "LTP mode: flag last trade older than (s)", "Flag only (LTP_OLD_TRADE): the order is still sent at that LTP"],
  ["max_tick_age_s", "LTP mode: max tick age (s)", "Newest packet for the contract"],
  ["max_depth_age_s", "OFFER mode: max depth age (s)", "Newest depth snapshot for the contract"],
  ["max_spot_age_s", "Max underlying price age (s)", "Stale underlying withdraws the whole underlying"],
  ["band_buffer", "Subscription buffer (strikes)", "Extra strikes subscribed beyond the window — kept warm, never tradeable"],
  ["near_opportunity_threshold", "Near opportunity threshold (≤ 0)", "Also show pairs with 0 ≥ opportunity ≥ this (per price unit) as NEAR — analysis only, never executable"],
];

export default function OptionsSettings() {
  const status = usePoll("/status", { ms: 5000 });
  const [s, setS] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [unds, setUnds] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    optionsApi.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(optionsError(e)));
    optionsApi.get("/universes").then((r) => setMarkets(r.data.markets || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!s?.universe) return;
    optionsApi.get("/underlyings", { params: { universe: s.universe } }).then((r) => setUnds(r.data.underlyings)).catch(() => setUnds([]));
  }, [s?.universe]);
  if (!s) return <div className="p-6 text-sm text-[var(--term-muted)]">Loading settings…</div>;
  const universes = (markets.find((m) => m.market === s.market) || {}).universes || [];
  const u = universes.find((x) => x.name === s.universe);
  const set = (k, v) => setS({ ...s, [k]: v });
  const selected = new Set(s.underlyings || []);
  const toggle = (n) => {
    const next = new Set(selected);
    next.has(n) ? next.delete(n) : next.add(n);
    set("underlyings", next.size ? [...next] : null);
  };
  const expiries = [...new Set(unds.flatMap((x) => x.expiries || []))].sort();
  const save = () => {
    setSaving(true);
    const body = { ...s };
    delete body.version;
    for (const [k] of NUM) body[k] = Number(body[k]);
    body.margin_top_n = Number(body.margin_top_n);
    body.risk_free_rate_pct = Number(body.risk_free_rate_pct);
    for (const k of ["fair_value_iv_pct", "allot_funds", "spread_to_maintain", ...SELL_WINDOW.map(([f]) => f)]) {
      body[k] = body[k] === "" || body[k] === null || body[k] === undefined ? null : Number(body[k]);
    }
    optionsApi.put("/settings", body).then((r) => { setS(r.data); toast.success("Settings saved"); status.reload(); })
      .catch((e) => toast.error(optionsError(e))).finally(() => setSaving(false));
  };
  // market and universe stay as chosen; everything else becomes the recommended value (saved only on Save)
  const resetDefaults = () => optionsApi.get("/settings/defaults", { params: { universe: s.universe } })
    .then((r) => { setS({ ...r.data, market: s.market, universe: s.universe, version: s.version }); toast.success("Recommended settings loaded. Press Save settings to keep them."); })
    .catch((e) => toast.error(optionsError(e)));
  // scanner audit B6: an opt-in preset fills the form like the defaults do (market and universe kept); Save keeps it
  const loadPreset = (name) => optionsApi.get("/settings/presets", { params: { universe: s.universe } })
    .then((r) => {
      const p = (r.data.presets || []).find((x) => x.name === name);
      if (!p) throw new Error(`preset ${name} not available`);
      setS({ ...p.settings, market: s.market, universe: s.universe, version: s.version });
      toast.success(`${p.label} loaded. Press Save settings to keep it.`);
    })
    .catch((e) => toast.error(optionsError(e)));
  const input = "bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1 text-xs";
  return (
    <div className="p-4 space-y-3 max-w-5xl" data-testid="options-settings-page">
      <h1 className="text-lg font-bold flex items-center gap-2"><Settings2 className="h-5 w-5" /> Scanner Settings</h1>
      <ScannerStatusBar status={status.data} onChanged={status.reload} />
      <div className="term-panel rounded-sm p-3 space-y-3 text-xs">
        <div className="grid md:grid-cols-4 gap-3">
          <label className="space-y-1">
            <div className="font-bold">Market</div>
            <div className="inline-flex rounded-sm border border-[var(--term-border-strong)] overflow-hidden font-bold" data-testid="options-setting-market">
              {(markets.length ? markets.map((m) => m.market) : ["MCX", "NFO"]).map((m) => (
                <button key={m} type="button" data-testid={`options-market-${m}`}
                  onClick={() => setS({ ...s, market: m, underlyings: null, universe: (((markets.find((x) => x.market === m) || {}).universes || [])[0] || {}).name || (m === "MCX" ? "MCX_OPTIONS" : "NIFTY50") })}
                  className={`px-4 py-1 ${s.market === m ? "bg-[#2563EB] text-white" : "bg-[var(--term-s1)]"}`}>{m}</button>
              ))}
            </div>
            <div className="text-[var(--term-muted)]">MCX options and NFO options.</div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Entry Price Mode</div>
            <select className={input} value={s.entry_price_mode} onChange={(e) => set("entry_price_mode", e.target.value)} data-testid="options-setting-mode">
              <option value="LTP">LTP — CE LTP + PE LTP</option><option value="OFFER">OFFER — CE best offer + PE best offer</option>
            </select>
            <div className="text-[var(--term-muted)]">The ONE price everything uses: the Scanner's rows, ranking and opportunity count, the Trade Ticket and the order. Only pairs with usable prices in this mode are listed.</div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Universe</div>
            <select className={input} value={s.universe || universes[0]?.name || ""} onChange={(e) => setS({ ...s, universe: e.target.value, underlyings: null })} data-testid="options-setting-universe">
              {universes.map((x) => <option key={x.name} value={x.name}>{x.label}</option>)}
            </select>
            {u && u.validation?.missing?.length > 0 && <div className="text-[#b38f00]">Not in today's master: {u.validation.missing.join(", ")}</div>}
          </label>
          <label className="space-y-1">
            <div className="font-bold">Expiry</div>
            <div className="flex gap-1">
              <select className={input} value={s.expiry_mode} onChange={(e) => set("expiry_mode", e.target.value)} data-testid="options-setting-expiry-mode">
                <option value="NEAREST">Nearest</option><option value="NEXT">Next</option><option value="EXPLICIT">Specific date</option>
                <option value="ALL_WITHIN">All expiries within…</option>
              </select>
              {s.expiry_mode === "ALL_WITHIN" && (
                <label className="flex items-center gap-1"><input className={`${input} w-16`} type="number" min="1" max="366"
                  value={s.max_expiry_days ?? 90} onChange={(e) => set("max_expiry_days", Number(e.target.value))}
                  data-testid="options-setting-max-expiry-days" /> days</label>
              )}
              {s.expiry_mode === "EXPLICIT" && (
                <select className={input} value={s.expiry_date || ""} onChange={(e) => set("expiry_date", e.target.value)}>
                  <option value="">choose…</option>{expiries.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              )}
            </div>
            <div className="text-[var(--term-muted)]">Per underlying; expired contracts are never listed. "All expiries within" scans every weekly and monthly expiry up to that many days (nearest first if the live-data budget runs short).</div>
          </label>
        </div>
        <div>
          <div className="font-bold mb-1 flex items-center gap-2">
            Underlyings {s.underlyings ? `(${s.underlyings.length} selected)` : "(all in universe)"}
            <button type="button" className="px-2 py-0.5 rounded-sm border border-[var(--term-border-strong)] font-normal" onClick={() => set("underlyings", null)} data-testid="options-underlyings-all">All</button>
          </div>
          <div className="flex flex-wrap gap-1 max-h-40 overflow-auto" data-testid="options-setting-underlyings">
            {unds.map((x) => (
              <button key={x.underlying} onClick={() => toggle(x.underlying)} disabled={!x.has_options}
                className={`px-2 py-0.5 rounded-sm border mono ${selected.has(x.underlying) ? "bg-[#2563EB] text-white border-[#2563EB]" : "border-[var(--term-border-strong)]"} ${!x.has_options ? "opacity-40" : ""}`}>
                {x.underlying}{x.economic_multiplier ? <span className="opacity-60"> ×{x.economic_multiplier}</span> : null}
              </button>
            ))}
          </div>
          <div className="text-[var(--term-muted)] mt-1">None selected = the whole universe. Start small on the live feed; the subscription cap is enforced.</div>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {NUM.slice(0, 1).map(([k, label, help]) => (
            <label key={k} className="space-y-1">
              <div className="font-bold">{label}</div>
              <input type="number" className={`${input} w-full`} value={s[k]} onChange={(e) => set(k, e.target.value)} data-testid={`options-setting-${k}`} />
              <div className="text-[var(--term-muted)]">{help}</div>
            </label>
          ))}
        </div>
        <div className="border-t border-[var(--term-border)] pt-2 space-y-1" data-testid="options-settings-sell-window">
          <div className="font-bold">SELL strike window (strikes from the ATM)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SELL_WINDOW.map(([k, label]) => (
              <label key={k} className="space-y-1">
                <div>{label}</div>
                <input type="number" min="0" max="25" step="1" placeholder="—" className={`${input} w-full`} value={s[k] ?? ""}
                  onChange={(e) => set(k, e.target.value)} data-testid={`options-setting-${k}`} />
              </label>
            ))}
          </div>
          <div className="text-[var(--term-muted)]">
            ATM = the strike nearest the underlying (exactly midway: the lower strike); it is always included and counted in neither side.
            CE ITM = strikes below the ATM, CE OTM = above; PE ITM = above, PE OTM = below. Set all four (0–25) or leave all blank
            (blank = Max ITM calls / puts strikes on each side). Each new SELL trade keeps these values for every pair it adds.
          </div>
        </div>
        <details className="border-t border-[var(--term-border)] pt-2" data-testid="options-settings-advanced">
          <summary className="cursor-pointer font-bold text-[var(--term-secondary)]">Advanced — feed freshness, strike window, broker capital, valuation</summary>
        <div className="grid md:grid-cols-4 gap-3 mt-2">
          {NUM.slice(1).map(([k, label, help]) => (
            <label key={k} className="space-y-1">
              <div className="font-bold">{label}</div>
              <input type="number" className={`${input} w-full`} value={s[k]} onChange={(e) => set(k, e.target.value)} data-testid={`options-setting-${k}`} />
              <div className="text-[var(--term-muted)]">{help}</div>
            </label>
          ))}
          <label className="space-y-1">
            <div className="font-bold">LTP mode: require volume today</div>
            <input type="checkbox" checked={!!s.require_volume_today} onChange={(e) => set("require_volume_today", e.target.checked)} />
          </label>
          <label className="space-y-1">
            <div className="font-bold">ATM rule</div>
            <div className="mono">STRICT — strike = underlying price is neither ITM</div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Broker capital (margin calculator)</div>
            <input type="checkbox" checked={!!s.margin_enabled} onChange={(e) => set("margin_enabled", e.target.checked)} data-testid="options-setting-margin_enabled" />
            <div className="text-[var(--term-muted)]">Read-only Zerodha calculator for BUY CE + BUY PE at one lot. Never places an order.</div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Capital: automatic for top N</div>
            <input type="number" className={`${input} w-full`} value={s.margin_top_n} onChange={(e) => set("margin_top_n", e.target.value)} data-testid="options-setting-margin_top_n" />
            <div className="text-[var(--term-muted)]">Top N of the active mode, refreshed every 60 s (0 = only on demand from a row).</div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Capital: product · price basis</div>
            <div className="flex gap-1">
              <select className={input} value={s.margin_product} onChange={(e) => set("margin_product", e.target.value)} data-testid="options-setting-margin_product">
                <option value="NRML">NRML</option><option value="MIS">MIS</option>
              </select>
              <select className={input} value={s.margin_price_basis} onChange={(e) => set("margin_price_basis", e.target.value)} data-testid="options-setting-margin_price_basis">
                <option value="OFFER_LIMIT">LIMIT at offer</option><option value="MARKET">MARKET</option>
              </select>
            </div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Valuation: risk-free rate %</div>
            <input type="number" className={`${input} w-full`} value={s.risk_free_rate_pct ?? ""} onChange={(e) => set("risk_free_rate_pct", e.target.value)} data-testid="options-setting-risk_free_rate_pct" />
            <div className="text-[var(--term-muted)]">The pricing model&apos;s r (Black-Scholes for NFO, Black-76 for MCX). Fair and 1-day values only.</div>
          </label>
          <label className="space-y-1">
            <div className="font-bold">Valuation: fair-value IV</div>
            <div className="flex gap-1">
              <select className={input} value={s.fair_value_iv_source || "ATM_IMPLIED"} onChange={(e) => set("fair_value_iv_source", e.target.value)} data-testid="options-setting-fair_value_iv_source">
                <option value="ATM_IMPLIED">Implied at the ATM strike</option><option value="CONFIGURED">Configured %</option>
              </select>
              {s.fair_value_iv_source === "CONFIGURED" &&
                <input type="number" className={`${input} w-20`} value={s.fair_value_iv_pct ?? ""} onChange={(e) => set("fair_value_iv_pct", e.target.value)} placeholder="IV %" data-testid="options-setting-fair_value_iv_pct" />}
            </div>
            <div className="text-[var(--term-muted)]">Zerodha supplies no IV: it is solved from the ATM CE/PE prices, or set here. No IV, no fair value - never the LTP.</div>
          </label>
          <label className="space-y-1 opacity-60">
            <div className="font-bold">Exit Price Mode (future execution)</div>
            <div className="mono">{s.exit_price_mode} — reserved, not used in V1</div>
          </label>
        </div>
        </details>
        <div className="flex items-center gap-2">
          <ResetDefaultsButton onReset={resetDefaults} testId="options-reset-defaults"
            title="Load the recommended scanner settings for this universe into the form. Nothing is saved until Save settings." />
          <button onClick={() => loadPreset("EXECUTABLE")} className="px-3 py-1.5 rounded-sm border border-[var(--term-border-strong)]"
            data-testid="options-preset-executable"
            title="Offer prices, quotes at most 10 s old, leg spread at most 2 %, 8 ITM strikes. Proposed values, pending market-hours measurement. Nothing is saved until Save settings.">Load Executable (LIVE) preset</button>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="options-settings-save">Save settings</button>
          <span className="text-[var(--term-muted)]">version {s.version} · saved per user · a running scanner applies it at once</span>
        </div>
      </div>
    </div>
  );
}
